import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Col, Input, message, Modal, Row, Select, Spin, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import debounce from 'lodash.debounce';
import api from '@/api/businessAdminInvoices';
import Pagination from '@/components/pagination';
import { searchIcon } from '@/constants/static';
import { downloadBatchInvoicePdf, type BatchInvoiceInput } from '@/utils/invoicePdf';
import './BusinessAdminInvoices.scss';

interface ListParams {
    pageNum: number;
    pageSize: number;
    keyword: string;
    sendStatus: string;
    paymentStatus: string;
}

interface InvoiceItem {
    businessAdminJobId: string;
    jobRefId?: string;
    businessAdminUserId?: string;
    businessAdminDisplayName?: string;
    productName?: string;
    assemblyCost?: number;
    completedTime?: string;
    businessAdminInvoiceId?: string;
    businessAdminInvoiceAmountCents?: number;
    businessAdminInvoiceStatus?: string;
    businessAdminInvoiceTime?: string;
    businessAdminInvoiceError?: string;
    businessAdminInvoiceStripeStatus?: string;
    businessAdminInvoiceHostedUrl?: string;
    businessAdminInvoicePaidTime?: string;
}

interface InvoiceBatchGroup {
    invoiceId: string;
    businessAdminDisplayName?: string;
    businessAdminInvoiceStatus?: string;
    businessAdminInvoiceStripeStatus?: string;
    businessAdminInvoiceTime?: string;
    businessAdminInvoicePaidTime?: string;
    totalAmountCents: number;
    jobs: InvoiceItem[];
}

const INVOICE_JOBS_INLINE_LIMIT = 3;
const INVOICE_JOBS_MODAL_PAGE_SIZE = 500;

const sendStatusOptions = [
    { value: '', label: 'All send status' },
    { value: 'SUCCEEDED', label: 'Sent' },
    { value: 'FAILED', label: 'Send failed' },
    { value: 'SKIPPED', label: 'Skipped' },
];

const paymentStatusOptions = [
    { value: '', label: 'All payment status' },
    { value: 'open', label: 'Unpaid' },
    { value: 'paid', label: 'Paid' },
    { value: 'void', label: 'Void' },
];

function toCents(value?: number | string | null): number {
    if (value == null || value === '') return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeInvoiceItem(row: InvoiceItem): InvoiceItem {
    return {
        ...row,
        businessAdminInvoiceAmountCents: toCents(row.businessAdminInvoiceAmountCents),
        assemblyCost: row.assemblyCost != null ? Number(row.assemblyCost) : undefined,
    };
}

const formatAud = (cents?: number | string | null) => {
    if (cents == null || cents === '') return '-';
    const value = toCents(cents);
    return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: 'AUD',
    }).format(value / 100);
};

const formatDate = (value?: string) => {
    if (!value) return '-';
    try {
        return new Date(value).toLocaleString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return value;
    }
};

const sendStatusLabel = (status?: string) => {
    if (!status) return '-';
    if (status === 'SUCCEEDED') return 'Sent';
    if (status === 'FAILED') return 'Send failed';
    if (status === 'SKIPPED') return 'Skipped';
    return status;
};

const paymentStatusLabel = (status?: string) => {
    if (!status) return 'Unknown';
    if (status === 'open') return 'Unpaid';
    if (status === 'paid') return 'Paid';
    if (status === 'void') return 'Void';
    if (status === 'uncollectible') return 'Uncollectible';
    return status;
};

function paymentStatusTagColor(status?: string): string {
    if (status === 'paid') return 'success';
    if (status === 'open') return 'warning';
    if (status === 'void' || status === 'uncollectible') return 'default';
    return 'processing';
}

function jobRefLabel(job: InvoiceItem): string {
    return job.jobRefId?.trim() || job.businessAdminJobId || '-';
}

function sumInvoiceCents(jobs: InvoiceItem[]): number {
    return jobs.reduce((sum, job) => sum + toCents(job.businessAdminInvoiceAmountCents), 0);
}

function groupRowsByInvoice(rows: InvoiceItem[]): InvoiceBatchGroup[] {
    const groups = new Map<string, InvoiceBatchGroup>();

    for (const row of rows) {
        const invoiceId = row.businessAdminInvoiceId || `job-${row.businessAdminJobId}`;
        let group = groups.get(invoiceId);
        if (!group) {
            group = {
                invoiceId,
                businessAdminDisplayName: row.businessAdminDisplayName,
                businessAdminInvoiceStatus: row.businessAdminInvoiceStatus,
                businessAdminInvoiceStripeStatus: row.businessAdminInvoiceStripeStatus,
                businessAdminInvoiceTime: row.businessAdminInvoiceTime,
                businessAdminInvoicePaidTime: row.businessAdminInvoicePaidTime,
                totalAmountCents: 0,
                jobs: [],
            };
            groups.set(invoiceId, group);
        }
        group.jobs.push(row);
        group.totalAmountCents += toCents(row.businessAdminInvoiceAmountCents);
    }

    return Array.from(groups.values());
}

const invoiceJobColumns: ColumnsType<InvoiceItem> = [
    {
        title: 'Job Ref ID',
        key: 'jobRefId',
        width: 140,
        render: (_, job) => <span style={{ fontWeight: 600 }}>{jobRefLabel(job)}</span>,
    },
    {
        title: 'Product',
        dataIndex: 'productName',
        key: 'productName',
        ellipsis: true,
        render: value => value || '-',
    },
    {
        title: 'Amount',
        key: 'amount',
        width: 110,
        align: 'right',
        render: (_, job) => formatAud(job.businessAdminInvoiceAmountCents),
    },
    {
        title: 'Completed',
        key: 'completedTime',
        width: 170,
        render: (_, job) => formatDate(job.completedTime),
    },
    {
        title: 'Business admin',
        dataIndex: 'businessAdminDisplayName',
        key: 'businessAdminDisplayName',
        width: 160,
        ellipsis: true,
        render: value => value || '-',
    },
];

const BusinessAdminInvoices = () => {
    const [loading, setLoading] = useState(false);
    const [totalRecord, setTotalRecord] = useState(0);
    const [tableData, setTableData] = useState<InvoiceItem[]>([]);
    const [listParams, setListParams] = useState<ListParams>({
        pageNum: 1,
        pageSize: 10,
        keyword: '',
        sendStatus: '',
        paymentStatus: '',
    });
    const [invoiceJobsModal, setInvoiceJobsModal] = useState<InvoiceBatchGroup | null>(null);
    const [invoiceJobsModalLoading, setInvoiceJobsModalLoading] = useState(false);
    const [invoiceJobsModalRows, setInvoiceJobsModalRows] = useState<InvoiceItem[]>([]);
    // Invoice id whose PDF is currently being generated, for the button spinner.
    const [downloadingId, setDownloadingId] = useState('');

    const invoiceGroups = useMemo(() => groupRowsByInvoice(tableData), [tableData]);
    const modalTotalCents = useMemo(() => sumInvoiceCents(invoiceJobsModalRows), [invoiceJobsModalRows]);

    const normalizeList = (responseData: any) => {
        if (Array.isArray(responseData?.records)) {
            const records = responseData.records.map((row: InvoiceItem) => normalizeInvoiceItem(row));
            return {
                records,
                total: Number(responseData.total ?? records.length ?? 0),
            };
        }
        return { records: [], total: 0 };
    };

    const getList = async (params: ListParams) => {
        setLoading(true);
        try {
            const query: Record<string, string | number> = {
                pageNum: params.pageNum,
                pageSize: params.pageSize,
            };
            if (params.keyword) query.keyword = params.keyword;
            if (params.sendStatus) query.sendStatus = params.sendStatus;
            if (params.paymentStatus) query.paymentStatus = params.paymentStatus;
            const res = await api.getList(query);
            const normalized = normalizeList(res.data);
            setTableData(normalized.records || []);
            setTotalRecord(normalized.total || 0);
        } catch (error: any) {
            message.error(error);
        } finally {
            setLoading(false);
        }
    };

    const openInvoiceJobsModal = async (group: InvoiceBatchGroup) => {
        setInvoiceJobsModal(group);
        setInvoiceJobsModalRows(group.jobs);
        if (!group.invoiceId.startsWith('job-')) {
            setInvoiceJobsModalLoading(true);
            try {
                const res = await api.getList({
                    pageNum: 1,
                    pageSize: INVOICE_JOBS_MODAL_PAGE_SIZE,
                    invoiceId: group.invoiceId,
                });
                const normalized = normalizeList(res.data);
                if (normalized.records.length > 0) {
                    setInvoiceJobsModalRows(normalized.records);
                }
            } catch (error: any) {
                message.error(error);
            } finally {
                setInvoiceJobsModalLoading(false);
            }
        }
    };

    const closeInvoiceJobsModal = () => {
        setInvoiceJobsModal(null);
        setInvoiceJobsModalRows([]);
        setInvoiceJobsModalLoading(false);
    };

    const toBatchInput = (group: InvoiceBatchGroup, jobs: InvoiceItem[]): BatchInvoiceInput => ({
        invoiceId: group.invoiceId,
        businessAdminDisplayName: group.businessAdminDisplayName,
        paymentStatus: group.businessAdminInvoiceStripeStatus,
        sentTime: group.businessAdminInvoiceTime,
        paidTime: group.businessAdminInvoicePaidTime,
        jobs: jobs.map(job => ({
            businessAdminJobId: job.businessAdminJobId,
            jobRefId: job.jobRefId,
            productName: job.productName,
            businessAdminInvoiceAmountCents: toCents(job.businessAdminInvoiceAmountCents),
            completedTime: job.completedTime,
        })),
    });

    /**
     * Download the consolidated invoice PDF for a partner so the admin can forward
     * it (e.g. after the company has paid). For a real invoice we pull every job in
     * the batch first, since the list page only shows this page's rows.
     */
    const handleDownloadGroup = async (group: InvoiceBatchGroup) => {
        setDownloadingId(group.invoiceId);
        try {
            let jobs = group.jobs;
            if (!group.invoiceId.startsWith('job-')) {
                const res = await api.getList({
                    pageNum: 1,
                    pageSize: INVOICE_JOBS_MODAL_PAGE_SIZE,
                    invoiceId: group.invoiceId,
                });
                const normalized = normalizeList(res.data);
                if (normalized.records.length > 0) {
                    jobs = normalized.records;
                }
            }
            await downloadBatchInvoicePdf(toBatchInput(group, jobs));
            message.success('Invoice downloaded');
        } catch (error: any) {
            message.error(error?.message || error || 'Could not generate the invoice');
        } finally {
            setDownloadingId('');
        }
    };

    /** Download from inside the "Invoice jobs" modal, where all jobs are already loaded. */
    const handleDownloadModal = async () => {
        if (!invoiceJobsModal) return;
        setDownloadingId(invoiceJobsModal.invoiceId);
        try {
            await downloadBatchInvoicePdf(toBatchInput(invoiceJobsModal, invoiceJobsModalRows));
            message.success('Invoice downloaded');
        } catch (error: any) {
            message.error(error?.message || error || 'Could not generate the invoice');
        } finally {
            setDownloadingId('');
        }
    };

    useEffect(() => {
        getList(listParams);
    }, [listParams]);

    const debouncedKeyword = useCallback(
        debounce((value: string) => {
            setListParams(state => ({ ...state, keyword: value, pageNum: 1 }));
        }, 500),
        []
    );

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        debouncedKeyword(event.target.value);
    };

    const handlePageChange = (pageNo: number) => {
        setListParams(state => ({ ...state, pageNum: pageNo }));
    };

    return (
        <div className="page-main account">
            <div className="page-title">Business Admin Invoices</div>
            <p className="muted" style={{ marginBottom: 16 }}>
                Consolidated invoices are sent every 7 days per business admin. Multiple jobs may share one invoice ID.
            </p>
            <div className="select-bar">
                <div className="select-bar-left" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <Input
                        className="search-input"
                        placeholder="Search job ref id, product, invoice id..."
                        onChange={handleSearch}
                        prefix={searchIcon}
                        style={{ width: 320 }}
                    />
                    <Select
                        value={listParams.sendStatus}
                        style={{ width: 200 }}
                        options={sendStatusOptions}
                        onChange={value =>
                            setListParams(state => ({
                                ...state,
                                sendStatus: value,
                                pageNum: 1,
                            }))
                        }
                    />
                    <Select
                        value={listParams.paymentStatus}
                        style={{ width: 200 }}
                        options={paymentStatusOptions}
                        onChange={value =>
                            setListParams(state => ({
                                ...state,
                                paymentStatus: value,
                                pageNum: 1,
                            }))
                        }
                    />
                </div>
            </div>

            {loading ? (
                <div className="data_loading flex-center">
                    <Spin />
                </div>
            ) : (
                <div className="table">
                    {totalRecord ? (
                        <>
                            <div className="table-th">
                                <Row>
                                    <Col span={4}>Invoice ID</Col>
                                    <Col span={3}>Business admin</Col>
                                    <Col span={6}>Jobs</Col>
                                    <Col span={2}>Total</Col>
                                    <Col span={2}>Send status</Col>
                                    <Col span={2}>Payment</Col>
                                    <Col span={3}>Sent at</Col>
                                    <Col span={2}>Paid at</Col>
                                </Row>
                            </div>
                            {invoiceGroups.map(group => (
                                <Row className="table-tr" key={group.invoiceId}>
                                    <Col span={4}>
                                        {group.invoiceId.startsWith('job-') ? (
                                            '-'
                                        ) : (
                                            <span title={group.invoiceId}>{group.invoiceId}</span>
                                        )}
                                    </Col>
                                    <Col span={3}>{group.businessAdminDisplayName || '-'}</Col>
                                    <Col span={6}>
                                        <div style={{ display: 'grid', gap: 4 }}>
                                            {group.jobs.slice(0, INVOICE_JOBS_INLINE_LIMIT).map((job, index) => (
                                                <div key={`${job.businessAdminJobId}-${index}`}>
                                                    <strong>{jobRefLabel(job)}</strong>
                                                    <span className="font-c97"> — {job.productName || '-'}</span>
                                                    <span className="font-c97">
                                                        {' '}
                                                        ({formatAud(job.businessAdminInvoiceAmountCents)})
                                                    </span>
                                                </div>
                                            ))}
                                            {group.jobs.length >= 2 ? (
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    style={{ padding: 0, height: 'auto', textAlign: 'left' }}
                                                    onClick={() => void openInvoiceJobsModal(group)}
                                                >
                                                    {group.jobs.length > INVOICE_JOBS_INLINE_LIMIT
                                                        ? `+${
                                                              group.jobs.length - INVOICE_JOBS_INLINE_LIMIT
                                                          } more · View all jobs (${group.jobs.length})`
                                                        : `View all jobs (${group.jobs.length})`}
                                                </Button>
                                            ) : null}
                                            <Button
                                                type="link"
                                                size="small"
                                                loading={downloadingId === group.invoiceId}
                                                style={{
                                                    padding: 0,
                                                    height: 'auto',
                                                    textAlign: 'left',
                                                    fontWeight: 600,
                                                }}
                                                onClick={() => void handleDownloadGroup(group)}
                                            >
                                                Download invoice (PDF)
                                            </Button>
                                        </div>
                                    </Col>
                                    <Col span={2}>
                                        <div style={{ fontWeight: 600 }}>{formatAud(group.totalAmountCents)}</div>
                                    </Col>
                                    <Col span={2}>{sendStatusLabel(group.businessAdminInvoiceStatus)}</Col>
                                    <Col span={2}>{paymentStatusLabel(group.businessAdminInvoiceStripeStatus)}</Col>
                                    <Col span={3}>{formatDate(group.businessAdminInvoiceTime)}</Col>
                                    <Col span={2}>{formatDate(group.businessAdminInvoicePaidTime)}</Col>
                                </Row>
                            ))}
                            <Pagination total={totalRecord} pageChange={handlePageChange} pageNo={listParams.pageNum} />
                        </>
                    ) : (
                        <div className="no-data flex-center">No data</div>
                    )}
                </div>
            )}

            <Modal
                className="invoice-jobs-modal"
                title="Invoice jobs"
                open={Boolean(invoiceJobsModal)}
                onCancel={closeInvoiceJobsModal}
                footer={[
                    <Button
                        key="download"
                        loading={!!invoiceJobsModal && downloadingId === invoiceJobsModal.invoiceId}
                        onClick={() => void handleDownloadModal()}
                    >
                        Download invoice (PDF)
                    </Button>,
                    <Button key="close" type="primary" onClick={closeInvoiceJobsModal}>
                        Close
                    </Button>,
                ]}
                width={920}
                centered
                destroyOnClose
            >
                {invoiceJobsModal ? (
                    <div className="invoice-jobs-modal">
                        <div className="invoice-jobs-modal__summary">
                            <div className="invoice-jobs-modal__stat">
                                <div className="invoice-jobs-modal__stat-label">Jobs</div>
                                <div className="invoice-jobs-modal__stat-value">{invoiceJobsModalRows.length}</div>
                            </div>
                            <div className="invoice-jobs-modal__stat">
                                <div className="invoice-jobs-modal__stat-label">Total amount</div>
                                <div className="invoice-jobs-modal__stat-value">{formatAud(modalTotalCents)}</div>
                            </div>
                            <div className="invoice-jobs-modal__stat">
                                <div className="invoice-jobs-modal__stat-label">Payment status</div>
                                <div className="invoice-jobs-modal__stat-value">
                                    <Tag
                                        color={paymentStatusTagColor(invoiceJobsModal.businessAdminInvoiceStripeStatus)}
                                    >
                                        {paymentStatusLabel(invoiceJobsModal.businessAdminInvoiceStripeStatus)}
                                    </Tag>
                                </div>
                            </div>
                        </div>

                        {!invoiceJobsModal.invoiceId.startsWith('job-') ? (
                            <code className="invoice-jobs-modal__invoice-id">{invoiceJobsModal.invoiceId}</code>
                        ) : null}

                        {invoiceJobsModalLoading ? (
                            <div className="invoice-jobs-modal__loading">
                                <Spin tip="Loading jobs…" />
                            </div>
                        ) : (
                            <div className="invoice-jobs-modal__table-wrap">
                                <Table<InvoiceItem>
                                    rowKey={job => job.businessAdminJobId}
                                    columns={invoiceJobColumns}
                                    dataSource={invoiceJobsModalRows}
                                    pagination={
                                        invoiceJobsModalRows.length > 10
                                            ? {
                                                  pageSize: 10,
                                                  showSizeChanger: false,
                                                  showTotal: total => `${total} jobs`,
                                              }
                                            : false
                                    }
                                    size="middle"
                                    scroll={{ x: 760, y: 360 }}
                                />
                            </div>
                        )}
                    </div>
                ) : null}
            </Modal>
        </div>
    );
};

export default BusinessAdminInvoices;
